<template>
  <LoaderComponent v-if="!isClinicLoaded" />

  <div v-else :class="style.clinicComponent">
    <SectionHeaderBlock title="Ваша клиника">
      <template #buttons>
        <PrimaryButton
          text="Сохранить"
          :disabled="!canSaveClinic"
          @click="saveHandler"
        />
      </template>
    </SectionHeaderBlock>

    <div :class="style.contentList">
      <section :class="style.contentBlock">
        <span :class="style.namePart">Логотип</span>

        <div :class="style.flexContainer">
          <SecondaryButton
            :icon="imageIcon"
            :class="[style.buttonUpload, logoImage ? style.hideIcon : '']"
            :style="{
              backgroundImage: logoImage ? `url(${logoImage})` : ''
            }"
            @click="openFilePicker"
          />

          <SecondaryButton
            :icon="uploadIcon"
            text="Загрузить"
            @click="openFilePicker"
          />

          <input
            ref="fileInput"
            type="file"
            name="photoLogo"
            id="upload-photo"
            accept="image/*"
            style="display: none"
            @change="uploadLogo"
          />
        </div>
      </section>

      <section :class="style.contentBlock">
        <InputStyle
          v-model="clinic.clinic"
          description="Название клиники"
          size="M"
          placeholder="Ваша клиника"
        />
      </section>

      <section :class="style.contentBlock">
        <SelectBase
          comment="Валюта"
          :text="currencySelectText"
          :items="valutaItems"
          :style="{width: '200px'}"
          @select="handleSelectCurrency"
        />
      </section>

      <section :class="style.contentBlock">
        <AccordionBase
          text="Адрес"
          showChevronWithActions
          :defaultOpen="false"
          :isHideActions="false"
        >
          <DragDropListBase
            :model-value="addressDragItems"
            item-key-field="__dragKey"
            name-field="name"
            value-field="value"
            name-placeholder="Введите адрес"
            value-placeholder=""
            suffix=""
            value-input-mode="text"
            @update:modelValue="handleAddressChange"
          >
            <template #actions="{remove}">
              <ButtonIcon :icon="closeIcon" size="28" @click="remove" />
            </template>
          </DragDropListBase>

          <template #actions>
            <ButtonIcon
              :icon="addIcon"
              @click="addAddress"
              variant="ghost"
              size="28"
            />
          </template>
        </AccordionBase>
      </section>

      <section :class="style.contentBlock">
        <AccordionBase
          text="Специализации"
          showChevronWithActions
          :defaultOpen="false"
          :isHideActions="false"
        >
          <DragDropListBase
            :model-value="specializationDragItems"
            item-key-field="__dragKey"
            name-field="name"
            value-field="value"
            name-placeholder="Введите специализацию"
            value-placeholder=""
            suffix=""
            value-input-mode="text"
            @update:modelValue="handleSpecializationChange"
          >
            <template #actions="{remove}">
              <ButtonIcon :icon="closeIcon" size="28" @click="remove" />
            </template>
          </DragDropListBase>

          <template #actions>
            <ButtonIcon
              :icon="addIcon"
              @click="addSpecialization"
              variant="ghost"
              size="28"
            />
          </template>
        </AccordionBase>
      </section>
    </div>
  </div>

  <ActionModal
    v-model="isUnsavedChangesModalOpen"
    title="Сохранить изменения?"
    subtitle="Вы внесли изменения на странице. Если перейти на другую страницу, они не сохранятся."
    :actions="saveModalsButtons"
  />
</template>

<script lang="ts">
import type {clinic} from "./ClinicTypes";
import {DOMAIN} from "@/http";
import style from "./ClinicStyle.module.scss";
import {defineComponent} from "vue";

import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import SelectBase from "@/elements/UI/select/SelectBase.vue";
import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import DragDropListBase from "@/elements/UI/list/drag/List/DragDropListBase.vue";
import ActionModal from "@/layout/nav/components/modal/modals/ActionModal/ActionModal.vue";

import imageIcon from "$images/icons/button/Name=Image, Size=16, Tone=Default.svg";
import uploadIcon from "$images/icons/button/Name=Upload, Size=16, Tone=Default.svg";
import closeIcon from "$images/icons/button/Name=Close, Size=16, Tone=Default.svg";
import addIcon from "$images/icons/button/Name=Add, Size=16, Tone=Default.svg";
import SectionHeaderBlock from "@/elements/UI/section/SectionHeaderBlock/SectionHeaderBlock.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import LoaderComponent from "@/elements/UI/overlay/Loader/LoaderComponent.vue";
import {API} from "@/api/generated";

type DragTextItem = {
  name: string;
  value: string;
  __dragKey: string;
};

export default defineComponent({
  name: "ClinicComponent",

  components: {
    LoaderComponent,
    ButtonIcon,
    SectionHeaderBlock,
    ActionModal,
    DragDropListBase,
    AccordionBase,
    SelectBase,
    InputStyle,
    SecondaryButton,
    PrimaryButton
  },

  data() {
    return {
      style,

      clinicStr: "",
      initialClinicRawSnapshot: "",
      DOMAIN,
      imageIcon,
      uploadIcon,
      closeIcon,
      addIcon,
      logoPreviewUrl: "",
      addressDragKeys: [] as string[],
      specializationDragKeys: [] as string[],
      valutaItems: [
        {
          text: "Рубль — ₽",
          icon: "",
          value: "Рубль",
          variant: "tab"
        },
        {
          text: "Тенге — ₸",
          icon: "",
          value: "Тенге",
          variant: "tab"
        },
        {
          text: "Доллар — $",
          icon: "",
          value: "Доллар",
          variant: "tab"
        },
        {
          text: "Евро — €",
          icon: "",
          value: "Евро",
          variant: "tab"
        }
      ] as SelectItem[],
      saveStatus: false,
      isSaving: false,
      isUnsavedChangesModalOpen: false,
      isRouteLeaveAllowed: false,
      pendingRoutePath: "",
      removeRouteGuard: null as (() => void) | null
    };
  },

  mounted() {
    this.registerRouteGuard();
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("keydown", this.handleSaveShortcut);

    this.normalizeClinicLists();
    this.syncClinicDragKeys();

    this.setInitialClinicSnapshot();
  },

  beforeUnmount() {
    this.removeRouteGuard?.();
    this.removeRouteGuard = null;

    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("keydown", this.handleSaveShortcut);
    this.revokeLogoPreview();
  },

  methods: {
    handleSaveShortcut(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.code === "KeyS") {
        event.preventDefault();
        void this.saveHandler();
      }
    },

    registerRouteGuard(): void {
      this.removeRouteGuard?.();

      this.removeRouteGuard = this.$router.beforeEach((to, from, next) => {
        if (to.fullPath === from.fullPath) {
          next();
          return;
        }

        if (this.isRouteLeaveAllowed || !this.hasUnsavedChanges) {
          next();
          return;
        }

        this.pendingRoutePath = to.fullPath;
        this.isUnsavedChangesModalOpen = true;

        next(false);
      });
    },

    handleBeforeUnload(event: BeforeUnloadEvent): void {
      if (!this.hasUnsavedChanges || this.isSaving) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    },

    closeUnsavedChangesModal(): void {
      this.isUnsavedChangesModalOpen = false;
    },

    goToPendingRoute(): void {
      const pendingRoutePath = this.pendingRoutePath;

      this.pendingRoutePath = "";
      this.closeUnsavedChangesModal();

      if (!pendingRoutePath) {
        return;
      }

      this.isRouteLeaveAllowed = true;

      this.$router.push(pendingRoutePath).finally(() => {
        this.isRouteLeaveAllowed = false;
      });
    },

    restoreInitialClinicState(): void {
      const snapshot = this.initialClinicRawSnapshot || this.clinicStr;

      if (!snapshot) {
        return;
      }

      try {
        const restoredClinic = JSON.parse(snapshot) as clinic;
        const currentClinic = this.clinic as unknown as Record<string, unknown>;
        const nextClinic = restoredClinic as unknown as Record<string, unknown>;

        Object.keys(currentClinic).forEach((key) => {
          if (!(key in nextClinic)) {
            delete currentClinic[key];
          }
        });

        Object.assign(this.clinic, restoredClinic);

        this.normalizeClinicLists();
        this.syncClinicDragKeys();

        this.setInitialClinicSnapshot();
        this.saveStatus = false;
      } catch (error) {
        console.error("Ошибка при восстановлении клиники:", error);
      }
    },

    leaveWithoutSaving(): void {
      if (this.isSaving) {
        return;
      }

      this.restoreInitialClinicState();
      this.goToPendingRoute();
    },

    async saveAndLeave(): Promise<void> {
      if (this.isSaving) {
        return;
      }

      const isSaved = await this.saveHandler();

      if (!isSaved) {
        return;
      }

      this.goToPendingRoute();
    },

    openFilePicker(): void {
      const input = this.$refs.fileInput as HTMLInputElement | undefined;
      input?.click();
    },

    revokeLogoPreview(): void {
      if (!this.logoPreviewUrl) {
        return;
      }

      URL.revokeObjectURL(this.logoPreviewUrl);
      this.logoPreviewUrl = "";
    },

    normalizeClinicLists(): void {
      if (!Array.isArray(this.clinic.addressList)) {
        this.clinic.addressList = [];
      }

      if (!Array.isArray(this.clinic.specializationList)) {
        this.clinic.specializationList = [];
      }
    },

    getDragKey(prefix: string, index: number): string {
      return `${prefix}_${index}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
    },

    syncClinicDragKeys(): void {
      this.syncDragKeys(
        this.clinic.addressList,
        this.addressDragKeys,
        "address"
      );
      this.syncDragKeys(
        this.clinic.specializationList,
        this.specializationDragKeys,
        "specialization"
      );
    },

    syncDragKeys(list: string[], keys: string[], prefix: string): void {
      if (!Array.isArray(list)) {
        keys.splice(0);
        return;
      }

      while (keys.length < list.length) {
        keys.push(this.getDragKey(prefix, keys.length));
      }

      if (keys.length > list.length) {
        keys.splice(list.length);
      }
    },

    makeDragTextItems(
      list: string[],
      keys: string[],
      prefix: string
    ): DragTextItem[] {
      if (!Array.isArray(list)) {
        return [];
      }

      return list.map((item, index) => ({
        name: String(item ?? ""),
        value: "",
        __dragKey: keys[index] || this.getDragKey(prefix, index)
      }));
    },

    getTextListFromDragItems(items: DragTextItem[]): string[] {
      return items.map((item) => String(item.name ?? ""));
    },

    getDragKeysFromDragItems(items: DragTextItem[], prefix: string): string[] {
      return items.map((item, index) => {
        return item.__dragKey || this.getDragKey(prefix, index);
      });
    },

    handleAddressChange(updatedList: DragTextItem[]): void {
      this.clinic.addressList = this.getTextListFromDragItems(updatedList);
      this.addressDragKeys = this.getDragKeysFromDragItems(
        updatedList,
        "address"
      );
    },

    handleSpecializationChange(updatedList: DragTextItem[]): void {
      this.clinic.specializationList =
        this.getTextListFromDragItems(updatedList);
      this.specializationDragKeys = this.getDragKeysFromDragItems(
        updatedList,
        "specialization"
      );
    },

    addAddress(): void {
      this.normalizeClinicLists();

      this.clinic.addressList.push("");
      this.addressDragKeys.push(
        this.getDragKey("address", this.clinic.addressList.length - 1)
      );
    },

    addSpecialization(): void {
      this.normalizeClinicLists();

      this.clinic.specializationList.push("");
      this.specializationDragKeys.push(
        this.getDragKey(
          "specialization",
          this.clinic.specializationList.length - 1
        )
      );
    },

    getLogoUrl(logo: string): string {
      return `https://${this.DOMAIN}/api/img?size=250x250&name=${encodeURIComponent(logo)}`;
    },

    getCurrencyObject(value: string): {name: string; symbol: string} {
      switch (value) {
        case "Доллар":
          return {name: "Доллар", symbol: "$"};
        case "Евро":
          return {name: "Евро", symbol: "€"};
        case "Тенге":
          return {name: "Тенге", symbol: "₸"};
        case "Рубль":
        default:
          return {name: "Рубль", symbol: "₽"};
      }
    },

    handleSelectCurrency(payload: SelectBasePayload): void {
      if (typeof payload.value !== "string") {
        return;
      }

      this.clinic.currency = this.getCurrencyObject(payload.value);
    },

    async uploadLogo(event: Event): Promise<void> {
      const target = event.target as HTMLInputElement | null;
      const file = target?.files?.[0];

      if (!file || !this.clinic?._id) {
        return;
      }

      if (!file.type.startsWith("image/")) {
        if (target) {
          target.value = "";
        }

        return;
      }

      this.revokeLogoPreview();
      this.logoPreviewUrl = URL.createObjectURL(file);

      const fileNameParts = file.name.split(".");
      const extension = fileNameParts.pop() || "jpg";
      const random = String(Math.floor(Math.random() * 1000));

      const newFile = new File(
        [file],
        `${this.clinic._id}_Logo_${random}.${extension}`,
        {type: file.type}
      );

      const formData = new FormData();

      formData.append("file", newFile);

      try {
        const response = await API.updateClinic({body: formData});

        this.clinic.logo = response?.logo || newFile.name;
        this.setInitialClinicSnapshot();
      } catch (error) {
        this.revokeLogoPreview();
        console.error("Ошибка при загрузке логотипа клиники:", error);
      } finally {
        if (target) {
          target.value = "";
        }
      }
    },

    async saveHandler(): Promise<boolean> {
      if (!this.canSaveClinic) {
        return false;
      }

      try {
        this.isSaving = true;

        await this.updateClinic();

        this.setInitialClinicSnapshot();
        this.saveStatus = false;

        return true;
      } catch (error) {
        console.error("Ошибка при сохранении клиники:", error);

        return false;
      } finally {
        this.isSaving = false;
      }
    },

    async updateClinic(): Promise<void> {
      this.normalizeClinicLists();

      this.clinic.addressList = this.clinic.addressList
        .map((item) => String(item ?? "").trim())
        .filter(Boolean);

      this.clinic.specializationList = this.clinic.specializationList
        .map((item) => String(item ?? "").trim())
        .filter(Boolean);

      this.syncClinicDragKeys();

      const res = await API.updateClinic({
        body: this.clinic,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      this.clinicStr = this.getClinicSnapshotFromValue(res);
      await this.$userStore.fetchUser();
    },

    setInitialClinicSnapshot(): void {
      this.clinicStr = this.getClinicSnapshot();
      this.initialClinicRawSnapshot = JSON.stringify(this.clinic);
      this.saveStatus = false;
    },

    getClinicSnapshot(): string {
      return this.getClinicSnapshotFromValue(this.clinic);
    },

    getClinicSnapshotFromValue(value: unknown): string {
      return JSON.stringify(this.normalizeSnapshotValue(value));
    },

    normalizeSnapshotValue(value: unknown): unknown {
      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeSnapshotValue(item));
      }

      if (value && typeof value === "object") {
        const objectValue = value as Record<string, unknown>;
        const sortedObject: Record<string, unknown> = {};

        Object.keys(objectValue)
          .sort()
          .forEach((key) => {
            sortedObject[key] = this.normalizeSnapshotValue(objectValue[key]);
          });

        return sortedObject;
      }

      return value;
    }
  },

  computed: {
    clinic(): clinic {
      return this.$clinicStore.getClinic;
    },

    isClinicLoaded(): boolean {
      return Boolean(this.clinic?._id);
    },

    logoImage(): string {
      if (this.logoPreviewUrl) {
        return this.logoPreviewUrl;
      }

      if (!this.clinic?.logo) {
        return "";
      }

      return this.getLogoUrl(this.clinic.logo);
    },

    addressDragItems(): DragTextItem[] {
      return this.makeDragTextItems(
        this.clinic.addressList,
        this.addressDragKeys,
        "address"
      );
    },

    specializationDragItems(): DragTextItem[] {
      return this.makeDragTextItems(
        this.clinic.specializationList,
        this.specializationDragKeys,
        "specialization"
      );
    },

    getClinicStr(): string {
      return this.getClinicSnapshot();
    },

    currencySelectText(): string {
      const name = this.clinic?.currency?.name || "Рубль";
      const symbol = this.clinic?.currency?.symbol || "₽";

      return `${name} — ${symbol}`;
    },

    hasUnsavedChanges(): boolean {
      return this.saveStatus;
    },

    canSaveClinic(): boolean {
      return this.hasUnsavedChanges && !this.isSaving;
    },
    saveModalsButtons(): ListActionItem[] {
      return [
        {
          text: "Не сохранять",
          disabled: this.isSaving,
          handler: this.leaveWithoutSaving
        },
        {
          text: "Сохранить",
          disabled: this.isSaving,
          handler: this.saveAndLeave
        }
      ];
    }
  },

  watch: {
    clinic(data: clinic) {
      if (!Array.isArray(data.addressList)) {
        data.addressList = [];
      }

      if (!Array.isArray(data.specializationList)) {
        data.specializationList = [];
      }

      this.syncClinicDragKeys();

      this.setInitialClinicSnapshot();
      this.revokeLogoPreview();
    },

    getClinicStr(data: string) {
      if (data !== this.clinicStr) {
        this.saveStatus = true;
      }
    }
  }
});
</script>
